import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import { fetchAdminScraperStagingBatches } from '../../../services/Http/AdminApi';

const AdminScraperStaging = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchAdminScraperStagingBatches({ per_page: 50, status, search });
      setRows(response?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load scraper staging batches');
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Scraper Staging</PageTitle>
      </PageHeader>
      <PageContent>
        <AdminFilterBar>
          <FilterRow>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="pending_review">Pending review</option>
              <option value="ready_to_process">Ready to process</option>
              <option value="processed">Processed</option>
              <option value="processed_with_errors">Processed with errors</option>
            </Select>
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by source or organization"
            />
          </FilterRow>
          <MainButton size="small" onClick={load}>Refresh</MainButton>
        </AdminFilterBar>

        <AdminDataTable
          loading={loading}
          rows={rows}
          emptyText="No staging batches found"
          columns={[
            { key: 'id', label: 'Batch ID', render: (r) => <Code>{r.id}</Code> },
            { key: 'source_name', label: 'Source' },
            { key: 'organization_name', label: 'Organization' },
            { key: 'dataset_type', label: 'Dataset' },
            { key: 'status', label: 'Status', render: (r) => <Badge>{r.status}</Badge> },
            { key: 'total_rows', label: 'Rows', render: (r) => `${r.total_rows || 0}` },
            { key: 'processed_rows', label: 'Processed', render: (r) => `${r.processed_rows || 0}` },
            { key: 'created_at', label: 'Created', render: (r) => (r.created_at ? new Date(r.created_at).toLocaleString() : '—') },
            { key: 'actions', label: 'Actions', render: (r) => <RowLink to={`/scraper-staging/${r.id}`}>Open</RowLink> },
          ]}
        />
      </PageContent>
    </PageContainer>
  );
};

export default AdminScraperStaging;

const FilterRow = styled.div`
  display: flex;
  gap: 0.625rem;
  align-items: center;
  flex-wrap: wrap;
`;
const SearchInput = styled.input`
  width: 320px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 0.5625rem 0.75rem;
  background: ${({ theme }) => theme.colors.surface};
`;
const Select = styled.select`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 0.5625rem 0.75rem;
  background: ${({ theme }) => theme.colors.surface};
`;
const Badge = styled.span`
  display: inline-flex;
  padding: 0.1875rem 0.5rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
  font-weight: 700;
`;
const Code = styled.code`
  font-size: 0.75rem;
  word-break: break-all;
`;
const RowLink = styled(Link)`
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 600;
`;

